import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useWallet } from "@/contexts/WalletContext";
import { toast } from "sonner";
import { Mail, Check } from "lucide-react";

export default function AirdropSignup() {
  const { primaryAddress } = useWallet();
  const [email, setEmail] = useState("");
  const [count, setCount] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    api.get("/airdrop/count").then((r) => setCount(r.data.count)).catch(() => {});
    if (localStorage.getItem("emg_airdrop") === "1") setDone(true);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    if (!consent) {
      toast.error("Please tick the consent box to join the list");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/airdrop/signup", {
        email,
        wallet_address: primaryAddress || null,
        source: "web",
        consent: true,
      });
      setCount(data.count);
      setDone(true);
      localStorage.setItem("emg_airdrop", "1");
      if (data.already) toast.success("You're already on the list", { description: "We'll be in touch." });
      else toast.success("You're on the list!", { description: "Spread the word 🤝" });
    } catch (err) {
      const msg =
        err.response?.status === 429
          ? "Too many attempts — try again in a minute."
          : err.response?.data?.detail || err.response?.data?.error || "Couldn't submit — try again";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="border border-[#cfff04] bg-[#121212] p-6 md:p-8 relative overflow-hidden"
      data-testid="airdrop-signup"
    >
      <div className="absolute right-0 top-0 bottom-0 w-32 opacity-10 pointer-events-none">
        <div className="absolute inset-0 grid-bg" />
      </div>
      <div className="relative">
        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#cfff04] mb-3">
          // Early Holder Airdrop
        </div>
        <div className="grid grid-cols-12 gap-6 items-center">
          <div className="col-span-12 md:col-span-7">
            <h3 className="font-display text-2xl sm:text-3xl font-black uppercase tracking-tighter mb-2">
              Join the airdrop list.
            </h3>
            <p className="text-sm text-[#e2e2e2] max-w-md">
              Drop your email to hear about the <span className="text-[#cfff04] font-mono">planned</span> community
              airdrop. Allocations are a goal, not a guarantee, and may change. SPURR is a utility token — not an
              investment, no returns promised.
              {primaryAddress && (
                <span className="block mt-2 text-sm text-[#c4c4c4]">
                  // we'll auto-link <span className="text-[#cfff04]">{primaryAddress.slice(0, 6)}…{primaryAddress.slice(-4)}</span>
                </span>
              )}
            </p>
          </div>
          <form
            onSubmit={submit}
            className="col-span-12 md:col-span-5"
            data-testid="airdrop-form"
          >
            {done ? (
              <div
                className="border border-[#00ff41] bg-[#001a05] px-4 py-3 flex items-center gap-3"
                data-testid="airdrop-done"
              >
                <Check size={18} className="text-[#00ff41]" />
                <div>
                  <div className="font-mono text-xs uppercase tracking-widest text-[#00ff41]">
                    You're on the list.
                  </div>
                  <div className="text-sm text-[#e2e2e2] mt-0.5">
                    {count !== null ? `${count.toLocaleString()} holders signed up` : ""}
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Mail
                      size={14}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-[#d0d0d0]"
                    />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@earlyholder.io"
                      data-testid="airdrop-email"
                      className="w-full bg-[#0c0c0c] border border-[#3a3a3a] focus:border-[#cfff04] focus:outline-none pl-9 pr-3 py-3 font-mono text-sm"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={busy}
                    data-testid="airdrop-submit"
                    className="btn-brutal bg-[#cfff04] text-[#050505] font-bold uppercase tracking-wider text-xs px-5"
                  >
                    {busy ? "…" : "Notify me"}
                  </button>
                </div>
                <label className="mt-3 flex items-start gap-2 cursor-pointer" data-testid="airdrop-consent-label">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    data-testid="airdrop-consent"
                    className="mt-0.5 accent-[#cfff04]"
                  />
                  <span className="text-sm leading-relaxed text-[#c4c4c4]">
                    I agree to receive occasional email updates from SolSpur about the SPURR token and can
                    unsubscribe anytime (one click in every email). No spam, no selling your data.
                  </span>
                </label>
                {count !== null && count > 0 && (
                  <div className="mt-3 font-mono text-[11px] uppercase tracking-widest text-[#d0d0d0]" data-testid="airdrop-count">
                    {count.toLocaleString()} on the list
                  </div>
                )}
              </>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
